var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import FocusTrap from "focus-trap-react";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { DayPicker, useInput } from "react-day-picker";
import { usePopper } from "react-popper";
import styled from "styled-components";
import { enUS } from "date-fns/locale";
import { getLocaleCode, getValidDate, isDisabledDate, resolveLocaleModule, safeFormatDate, safeParseDate } from "./InputCalendarUtils";
const PopperDiv = styled.div `
background-color: white;
z-index: 999;
box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
`;
export { getValidDate, isDisabledDate };
const InputCalendarTS = forwardRef((props, ref) => {
    var _a;
    const [_locale, setLocale] = useState(enUS);
    const initialDate = getValidDate(props.initialValue);
    //Initialize props for Calendar and associated input
    const { inputProps, dayPickerProps, setSelected } = useInput({
        format: 'P',
        defaultSelected: initialDate
    });
    //Date input display value
    const [inputValue, setInputValue] = useState(initialDate ? safeFormatDate(initialDate, _locale) : '');
    const initialValueTimeRef = useRef(initialDate === null || initialDate === void 0 ? void 0 : initialDate.getTime());
    const suppressNextEmptyInitialValueSyncRef = useRef(false);
    //Input ref for popper
    const popperRef = useRef(null);
    //Element for popper to display
    const [popperElement, setPopperElement] = useState(null);
    //State controlling popper visibility
    const [isPopperOpen, setIsPopperOpen] = useState(false);
    //Create popper
    const popper = usePopper(popperRef.current, popperElement, {
        placement: 'top-start'
    });
    useEffect(() => {
        const locale = getLocaleCode(props.locale);
        let isMounted = true;
        const setCalendarLocale = (localeToSet) => {
            if (!isMounted) {
                return;
            }
            setLocale(localeToSet);
            setInputValue(currentInputValue => {
                const parsedInputValue = safeParseDate(currentInputValue, _locale);
                return parsedInputValue ? safeFormatDate(parsedInputValue, localeToSet) : currentInputValue;
            });
        };
        const importLocale = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const localeToSet = yield import('date-fns/locale/' + locale + '/index.js');
                setCalendarLocale(resolveLocaleModule(localeToSet));
            }
            catch (_a) {
                setCalendarLocale(enUS);
            }
        });
        if (_locale.code !== locale) {
            importLocale();
        }
        return () => {
            isMounted = false;
        };
    }, [props.locale]);
    useEffect(() => {
        const nextInitialDate = getValidDate(props.initialValue);
        const nextInitialTime = nextInitialDate === null || nextInitialDate === void 0 ? void 0 : nextInitialDate.getTime();
        if (initialValueTimeRef.current === nextInitialTime) {
            return;
        }
        initialValueTimeRef.current = nextInitialTime;
        setSelected(nextInitialDate);
        if (suppressNextEmptyInitialValueSyncRef.current && nextInitialTime === undefined) {
            suppressNextEmptyInitialValueSyncRef.current = false;
            return;
        }
        suppressNextEmptyInitialValueSyncRef.current = false;
        setInputValue(nextInitialDate ? safeFormatDate(nextInitialDate, _locale) : '');
    }, [props.initialValue]);
    const onFocus = () => {
        setIsPopperOpen(true);
    };
    const onBlur = () => {
        setIsPopperOpen(false);
    };
    const notifyDateChange = (date, isDisabled = false) => {
        if (typeof props.onDateChange === 'function') {
            props.onDateChange(date, isDisabled);
        }
    };
    //Handle Calendar combo box input change
    const handleInputChange = (e) => {
        var _a;
        const nextInputValue = e.currentTarget.value;
        setInputValue(nextInputValue);
        if (nextInputValue.trim() === '') {
            setSelected(undefined);
            notifyDateChange(undefined);
            return;
        }
        const date = safeParseDate(nextInputValue, _locale);
        if (!date) {
            setSelected(undefined);
            suppressNextEmptyInitialValueSyncRef.current = true;
            notifyDateChange(undefined);
            return;
        }
        if (isDisabledDate(date, (_a = props.modifiers) === null || _a === void 0 ? void 0 : _a.disabled)) {
            setSelected(undefined);
            suppressNextEmptyInitialValueSyncRef.current = true;
            notifyDateChange(undefined, true);
        }
        else {
            setSelected(date);
            notifyDateChange(date);
        }
    };
    const handleDaySelect = (day) => {
        var _a;
        const selectedDay = getValidDate(day);
        if (!selectedDay) {
            setSelected(undefined);
            setInputValue('');
            notifyDateChange(undefined);
            onBlur();
            return;
        }
        if (isDisabledDate(selectedDay, (_a = props.modifiers) === null || _a === void 0 ? void 0 : _a.disabled)) {
            setSelected(undefined);
            setInputValue('');
            notifyDateChange(undefined, true);
            onBlur();
            return;
        }
        setSelected(selectedDay);
        setInputValue(safeFormatDate(selectedDay, _locale));
        notifyDateChange(selectedDay);
        onBlur();
    };
    const disabledMatcher = ((_a = props.modifiers) === null || _a === void 0 ? void 0 : _a.disabled) ? (day) => isDisabledDate(day, props.modifiers.disabled) : undefined;
    return (React.createElement("div", { id: props.id },
        React.createElement("div", { ref: popperRef },
            React.createElement("input", Object.assign({}, inputProps, props.inputProps, { onFocus: onFocus, onChange: (e) => {
                    handleInputChange(e);
                }, value: inputValue, placeholder: _locale.formatLong.date({ width: 'short' }), ref: ref, id: props.id + "_input", autoComplete: "off" }))),
        isPopperOpen ?
            //Focus trap to handle focus
            React.createElement(FocusTrap, { active: true, focusTrapOptions: {
                    initialFocus: false,
                    allowOutsideClick: true,
                    clickOutsideDeactivates: true,
                    onDeactivate: onBlur,
                    returnFocusOnDeactivate: false
                } },
                React.createElement(PopperDiv, Object.assign({ tabIndex: -1, style: popper.styles.popper, className: 'input_popper_calendar' }, popper.attributes.popper, { ref: setPopperElement, role: "dialog" }),
                    React.createElement(DayPicker, Object.assign({}, dayPickerProps, { mode: 'single', onSelect: (day) => {
                            handleDaySelect(day);
                        }, fixedWeeks: true, modifiers: props.modifiers, disabled: disabledMatcher, locale: _locale }))))
            : null));
});
export default InputCalendarTS;
